import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type InboundEmailStatus = 'received' | 'routed' | 'processed' | 'failed' | 'skipped'

export interface InboundEmail {
  id: string
  s3_bucket: string
  s3_key: string
  received_at: string
  sender_email: string
  recipient_email: string
  recipient_source: string
  subject: string
  tenant_schema: string
  status: InboundEmailStatus
  status_label: string
  error_code: string
  error_message: string
  attempts: number
  processed_at: string | null
  related_type: '' | 'sii_notification' | 'inbound_dte'
  related_id: string | null
}

export interface InboundEmailDetail extends InboundEmail {
  headers: Record<string, string>
  received_headers: string[]
  headers_error?: string
}

interface ListResponse {
  items: InboundEmail[]
  total: number
  counts_by_status: Record<string, number>
}

export function useInboundEmails(params: {
  status?: string
  tenant_schema?: string
  search?: string
  days?: number
  limit?: number
} = {}) {
  return useQuery<ListResponse>({
    queryKey: ['inbound-emails', params],
    queryFn: async () => {
      const res = await apiClient.get('/platform/inbound-emails/', { params })
      return res.data
    },
    refetchInterval: 30_000,
  })
}

export function useInboundEmailDetail(id: string | null) {
  return useQuery<InboundEmailDetail>({
    queryKey: ['inbound-email', id],
    queryFn: async () => {
      const res = await apiClient.get(`/platform/inbound-emails/${id}/`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useReprocessInboundEmail() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (args: { id: string; tenant_schema?: string; recipient_email?: string }) => {
      const { id, ...body } = args
      const res = await apiClient.post(`/platform/inbound-emails/${id}/reprocess/`, body)
      return res.data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['inbound-emails'] })
    },
  })
}
